import {
  differenceInDays,
  differenceInHours,
  differenceInMinutes,
  differenceInSeconds,
} from 'date-fns'

import Localization from '~/client/src/shared/localization/LocalizationManager'
import { parseDateValue } from '~/client/src/shared/utils/dateValueParser'

import {
  IResourceFieldDefinition,
  IResourceRuntimeContext,
} from '../definitions'
import { getSensorStateLabel } from '../definitions/sensorState'

const EMPTY_VALUE = '-'

interface IFormatResourceFieldArgs {
  fieldDefinition?: IResourceFieldDefinition
  value: any
  fallbackDisplayValue: string
  runtimeContext: IResourceRuntimeContext
}

const normalizeDisplayValue = (value?: string): string =>
  isEmptyResourceValue(value) ? EMPTY_VALUE : value

const extractNumber = (
  value: any,
  fallbackDisplayValue: string,
): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value
  }

  const numericFallback = Number(fallbackDisplayValue)
  if (Number.isFinite(numericFallback)) {
    return numericFallback
  }

  return null
}

const formatRelativeTime = (
  value: any,
  fallbackDisplayValue: string,
  runtimeContext: IResourceRuntimeContext,
): string => {
  const telemetryDate = parseDateValue(value)
  if (!telemetryDate) {
    return fallbackDisplayValue
  }

  const now = runtimeContext.now || new Date()
  const secondsAgo = differenceInSeconds(now, telemetryDate)

  if (secondsAgo < 0) {
    return fallbackDisplayValue
  }

  if (secondsAgo < 60) {
    return Localization.translator.xSecondsAgoShort(secondsAgo)
  }

  const minutesAgo = Math.max(1, differenceInMinutes(now, telemetryDate))
  if (minutesAgo < 60) {
    return Localization.translator.xMinutesAgoShort(minutesAgo)
  }

  const hoursAgo = Math.max(1, differenceInHours(now, telemetryDate))
  if (hoursAgo < 24) {
    return Localization.translator.xHoursAgoShort(hoursAgo)
  }

  const daysAgo = Math.max(1, differenceInDays(now, telemetryDate))
  return Localization.translator.xDaysAgoShort(daysAgo)
}

const applyRound = (value: any, fallbackDisplayValue: string): string => {
  const numeric = extractNumber(value, fallbackDisplayValue)
  if (numeric === null) {
    return fallbackDisplayValue
  }

  return String(Math.round(numeric))
}

const applyPercent = (displayValue: string): string => {
  if (displayValue === EMPTY_VALUE || displayValue.includes('%')) {
    return displayValue
  }

  return `${displayValue}%`
}

const applySensorStateLabel = (value: any, displayValue: string): string => {
  if (displayValue === EMPTY_VALUE) {
    return displayValue
  }

  const key = typeof value === 'string' ? value : displayValue
  return getSensorStateLabel(key)
}

const celsiusToFahrenheit = (celsius: number): number => (celsius * 9) / 5 + 32

const applyTemperatureUnit = (
  value: any,
  displayValue: string,
  runtimeContext: IResourceRuntimeContext,
): string => {
  if (displayValue === EMPTY_VALUE) {
    return displayValue
  }

  if (displayValue.includes('°C') || displayValue.includes('°F')) {
    return displayValue
  }

  const { temperatureUnit } = runtimeContext

  if (temperatureUnit === '°F') {
    const numeric = extractNumber(value, displayValue)
    if (numeric !== null) {
      return `${Math.round(celsiusToFahrenheit(numeric))} °F`
    }
  }

  return `${displayValue} ${temperatureUnit}`
}

export const formatResourceField = ({
  fieldDefinition,
  value,
  fallbackDisplayValue,
  runtimeContext,
}: IFormatResourceFieldArgs): string => {
  const normalizedDisplayValue = normalizeDisplayValue(fallbackDisplayValue)

  if (!fieldDefinition?.format?.length) {
    return normalizedDisplayValue
  }

  const supportsRelativeTime = fieldDefinition.format.includes('relativeTime')
  if (normalizedDisplayValue === EMPTY_VALUE && !supportsRelativeTime) {
    return normalizedDisplayValue
  }

  return fieldDefinition.format.reduce((current, formatType) => {
    if (current === EMPTY_VALUE && formatType !== 'relativeTime') {
      return current
    }

    switch (formatType) {
      case 'round':
        return applyRound(value, current)
      case 'percent':
        return applyPercent(current)
      case 'temperatureUnitAware':
        return applyTemperatureUnit(value, current, runtimeContext)
      case 'relativeTime':
        return formatRelativeTime(value, current, runtimeContext)
      case 'sensorStateLabel':
        return applySensorStateLabel(value, current)
      default:
        return current
    }
  }, normalizedDisplayValue)
}

export const RESOURCE_EMPTY_VALUE = EMPTY_VALUE

export const isEmptyResourceValue = (value: any): boolean =>
  value === null || value === undefined || value === ''
